import type { AxiosError } from 'axios';
import { Button, Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import type { Offer } from '@/api/generated/models';
import { OfferStatus } from '@/api/generated/models/offerStatus';
import {
  getGetOfferQueryKey,
  getListOffersQueryKey,
  useCreateOffer,
  useGetOffer,
  useUpdateOffer
} from '@/api/generated/offers/offers';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { useListPriceLists } from '@/api/generated/price-lists/price-lists';
import {
  BackButton,
  ContentCard,
  DateField,
  FormActions,
  MutationButton,
  NumberField,
  PageHeading,
  SelectField,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';

const PAYMENT_TERMS_OPTIONS = ['15', '30', '45', '60', '90'];

const today = () => new Date().toISOString().slice(0, 10);

const schema = z.object({
  clientOrgId: z.string().min(1),
  objectCode: z.string().optional(),
  objectName: z.string().optional(),
  priceListId: z.string().optional(),
  name: z.string().min(1),
  inquiryReference: z.string().optional(),
  description: z.string().optional(),
  offerDate: z.string().min(1),
  validUntil: z.string().nullable().optional(),
  paymentTerms: z.string().min(1),
  notes: z.string().optional(),
  vatEnabled: z.boolean(),
  vatRate: z.string().optional()
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  clientOrgId: '',
  objectCode: '',
  objectName: '',
  priceListId: '',
  name: '',
  inquiryReference: '',
  description: '',
  offerDate: '',
  validUntil: null,
  paymentTerms: '30',
  notes: '',
  vatEnabled: false,
  vatRate: '25'
};

// Isti obrazac kao ostale forme — backend već vraća specifičan, lokalizirani razlog
// (npr. "Ponuda s ovim nazivom već postoji").
const offerErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

/**
 * Puna stranica za kreiranje I uređivanje ponude (Figma 4507:29617) — zamjena
 * za bivši OfferFormModal popup. Klijent i objekt/brod editabilni su pri
 * kreiranju i u edit modu SAMO dok je ponuda DRAFT (OFF-NEW-55) — nakon
 * REJECTED ostaju zaključani (vidi canEditClientObject), jer identitet posla
 * (tko/koji objekt) ostaje fiksan nakon prvog slanja, samo se sadržaj smije
 * ispraviti prije ponovnog slanja (ostala polja ostaju editabilna i nakon
 * REJECTED, isti gate kao canEdit na OfferDetailPage). Cjenik ostaje
 * nepromjenjiv nakon kreiranja (prikazuje se kao onemogućeno polje). Broj
 * ponude (OFF-01) generira backend automatski pri kreiranju — nema ga u
 * formi, vidljiv je tek na detalju. Nakon uspješnog spremanja (create ili
 * edit) korisnik se vraća na postojeći detail ekran ponude.
 */
export const OfferFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const { data: offerData, isLoading: isLoadingOffer } = useGetOffer(id ?? '', {
    query: { enabled: isEdit, refetchOnMount: 'always' }
  });
  const offer = offerData as Offer | undefined;

  const { data: connectionsData } = useListOrganisationConnections();
  const { data: priceListsData } = useListPriceLists();

  const clientOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  const priceListOptions = useMemo(
    () =>
      (priceListsData ?? [])
        .filter((pl) => pl.isActive)
        .map((pl) => ({ value: pl.id ?? '', label: pl.name ?? '—' })),
    [priceListsData]
  );

  // Edit mod: naziv trenutnog cjenika prikazujemo i ako više nije aktivan
  // (nije u priceListOptions) — cjenik se ne može ovdje mijenjati.
  const priceListName = useMemo(() => {
    if (!offer?.priceListId) return t('offerFormEdit.noPriceList');
    return (priceListsData ?? []).find((pl) => pl.id === offer.priceListId)?.name ?? '—';
  }, [priceListsData, offer?.priceListId, t]);

  // Klijent/objekt (OFF-NEW-55) editabilni su samo dok je DRAFT — nakon REJECTED ostaju
  // zaključani (identitet posla se ne mijenja, samo sadržaj prije ponovnog slanja).
  const canEditClientObject = !isEdit || offer?.status === OfferStatus.DRAFT;
  const objectDisplayValue =
    offer?.objectCode || offer?.objectName ? formatObjectLabel(offer) : t('createOffer.noObject');

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { ...emptyValues, offerDate: today() }
  });

  useEffect(() => {
    if (isEdit && offer) {
      form.setValues({
        clientOrgId: offer.clientOrgId ?? '',
        objectCode: offer.objectCode ?? '',
        objectName: offer.objectName ?? '',
        priceListId: offer.priceListId ?? '',
        name: offer.name ?? '',
        inquiryReference: offer.inquiryReference ?? '',
        description: offer.description ?? '',
        offerDate: offer.offerDate ?? today(),
        validUntil: offer.validUntil ?? null,
        paymentTerms: String(offer.paymentTerms ?? 30),
        notes: offer.notes ?? '',
        vatEnabled: offer.vatEnabled ?? false,
        vatRate: offer.vatRate != null ? String(offer.vatRate) : '25'
      });
    }
  }, [isEdit, offer]);

  const backTarget = isEdit && id ? `/offers/${id}` : '/offers';

  const createMutation = useCreateOffer({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListOffersQueryKey() });
        const createdId = (created as Offer | undefined)?.id;
        if (createdId) {
          navigate(`/offers/${createdId}`, {
            state: { savedMessage: t('createOffer.createSuccess') }
          });
        }
      }
    }
  });

  const updateMutation = useUpdateOffer({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOfferQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListOffersQueryKey() });
        navigate(backTarget, { state: { savedMessage: t('offerFormEdit.updateSuccess') } });
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    const validUntil =
      values.validUntil && values.validUntil.trim() !== '' ? values.validUntil : undefined;
    const vatEnabled = values.vatEnabled;
    const vatRate = vatEnabled && values.vatRate ? Number(values.vatRate) : undefined;

    if (isEdit && id) {
      updateMutation.mutate({
        id,
        data: {
          clientOrgId: values.clientOrgId,
          objectCode: values.objectCode || undefined,
          objectName: values.objectName || undefined,
          name: values.name,
          inquiryReference: values.inquiryReference,
          description: values.description,
          offerDate: values.offerDate,
          validUntil,
          paymentTerms: Number(values.paymentTerms),
          notes: values.notes,
          vatEnabled,
          vatRate
        }
      });
    } else {
      createMutation.mutate({
        data: {
          clientOrgId: values.clientOrgId,
          objectCode: values.objectCode || undefined,
          objectName: values.objectName || undefined,
          priceListId: values.priceListId || undefined,
          name: values.name,
          inquiryReference: values.inquiryReference,
          description: values.description,
          offerDate: values.offerDate,
          validUntil,
          paymentTerms: Number(values.paymentTerms),
          notes: values.notes,
          vatEnabled,
          vatRate
        }
      });
    }
  };

  if (isEdit && isLoadingOffer) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={backTarget} />

        <PageHeading
          title={t(isEdit ? 'offerFormEdit.title' : 'createOffer.title')}
          subtitle={t(
            !isEdit
              ? 'createOffer.subtitle'
              : canEditClientObject
                ? 'offerFormEdit.subtitle'
                : 'offerFormEdit.subtitleLocked'
          )}
        />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap={24}>
            {mutation.isError && (
              <Toast tone="danger">{offerErrorMessage(mutation.error, t('common.error'))}</Toast>
            )}

            <ContentCard>
              <Stack gap={16} p={20}>
                <Stack gap={4}>
                  <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {t('createOffer.section1Title')}
                  </Text>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {t('createOffer.section1Subtitle')}
                  </Text>
                </Stack>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    {canEditClientObject ? (
                      <SelectField
                        label={t('createOffer.fieldClient')}
                        required
                        allowDeselect={false}
                        placeholder={t('createOffer.fieldClientPlaceholder')}
                        data={clientOptions}
                        {...form.getInputProps('clientOrgId')}
                      />
                    ) : (
                      <TextField
                        label={t('createOffer.fieldClient')}
                        disabled
                        readOnly
                        value={offer?.clientOrgName ?? '—'}
                      />
                    )}
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    {canEditClientObject ? (
                      <TextField
                        label={t('createOffer.fieldObjectCode')}
                        placeholder={t('createOffer.fieldObjectCodePlaceholder')}
                        {...form.getInputProps('objectCode')}
                      />
                    ) : (
                      <TextField
                        label={t('createOffer.fieldObject')}
                        disabled
                        readOnly
                        value={objectDisplayValue}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    {canEditClientObject && (
                      <TextField
                        label={t('createOffer.fieldObjectName')}
                        placeholder={t('createOffer.fieldObjectNamePlaceholder')}
                        {...form.getInputProps('objectName')}
                      />
                    )}
                  </div>
                </div>

                <div className="form-row-2col">
                  {isEdit && (
                    <div style={{ flex: 1 }}>
                      <TextField
                        label={t('createOffer.fieldOfferNumber')}
                        disabled
                        readOnly
                        value={offer?.offerNumber ?? ''}
                      />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <SelectField
                      label={t('createOffer.fieldPaymentTerms')}
                      required
                      allowDeselect={false}
                      data={PAYMENT_TERMS_OPTIONS.map((days) => ({
                        value: days,
                        label: t('createOffer.paymentTermsValue', { days })
                      }))}
                      {...form.getInputProps('paymentTerms')}
                    />
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('createOffer.fieldName')}
                      required
                      {...form.getInputProps('name')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('createOffer.fieldInquiryReference')}
                      {...form.getInputProps('inquiryReference')}
                    />
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <DateField
                      label={t('createOffer.fieldOfferDate')}
                      valueFormat="DD.MM.YYYY."
                      required
                      {...form.getInputProps('offerDate')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DateField
                      label={t('createOffer.fieldValidUntil')}
                      valueFormat="DD.MM.YYYY."
                      clearable
                      {...form.getInputProps('validUntil')}
                    />
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <SelectField
                      label={t('createOffer.fieldVat')}
                      allowDeselect={false}
                      data={[
                        { value: 'false', label: t('createOffer.vatNo') },
                        { value: 'true', label: t('createOffer.vatYes') }
                      ]}
                      value={form.values.vatEnabled ? 'true' : 'false'}
                      onChange={(value) => form.setFieldValue('vatEnabled', value === 'true')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <NumberField
                      label={t('createOffer.fieldVatRate')}
                      disabled={!form.values.vatEnabled}
                      allowNegative={false}
                      allowDecimal={false}
                      suffix=" %"
                      {...form.getInputProps('vatRate')}
                    />
                  </div>
                </div>

                <TextareaField
                  label={t('createOffer.fieldDescription')}
                  minRows={2}
                  {...form.getInputProps('description')}
                />

                <TextareaField
                  label={t('createOffer.fieldNotes')}
                  minRows={2}
                  {...form.getInputProps('notes')}
                />
              </Stack>
            </ContentCard>

            <ContentCard>
              <Stack gap={16} p={20}>
                <Stack gap={4}>
                  <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {t('createOffer.section2Title')}
                  </Text>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {t('createOffer.section2Subtitle')}
                  </Text>
                </Stack>

                {isEdit ? (
                  <TextField
                    label={t('createOffer.fieldPriceList')}
                    disabled
                    readOnly
                    value={priceListName}
                  />
                ) : (
                  <Stack gap={6}>
                    <SelectField
                      label={t('createOffer.fieldPriceList')}
                      clearable
                      placeholder={t('createOffer.fieldPriceListPlaceholder')}
                      data={priceListOptions}
                      {...form.getInputProps('priceListId')}
                    />
                    <Text
                      component="a"
                      href="/templates/price-lists"
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('/templates/price-lists');
                      }}
                      fz={12.5}
                      fw={600}
                      style={{ color: 'var(--color-text-link)', cursor: 'pointer' }}
                    >
                      {t('createOffer.managePriceListsLink')}
                    </Text>
                  </Stack>
                )}
              </Stack>
            </ContentCard>

            <FormActions>
              <Button variant="outline" onClick={() => navigate(backTarget)}>
                {t('common.cancel')}
              </Button>
              <MutationButton
                type="submit"
                isPending={mutation.isPending}
                isPaused={mutation.isPaused}
              >
                {t(isEdit ? 'offerFormEdit.submitButton' : 'createOffer.submitButton')}
              </MutationButton>
            </FormActions>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
};
